<template>
  <div class="firmware-test-sheets">
    <v-card outlined class="mb-4">
      <v-card-title class="text-subtitle-1">{{ $t('firmware.test.firmware-title') }}</v-card-title>
      <v-card-subtitle>{{ $t('firmware.test.firmware-explained') }}</v-card-subtitle>
      <v-card-text>
        <div v-if="canDeployFirmware" class="d-flex align-center flex-wrap mb-4" style="gap: 12px">
          <v-combobox v-model="identifier" :items="identifierItems" :label="$t('firmware.test.identifier')" :hint="$t('firmware.test.identifier-hint')" persistent-hint outlined dense style="max-width: 360px" class="mono" @keyup.enter="start(identifier)" />
          <v-btn small color="primary" :disabled="!identifier" :loading="starting" @click="start(identifier)">{{ $t('firmware.test.start') }}</v-btn>
        </div>
        <div v-if="loading && !sheets.length" class="text-center my-4"><v-progress-circular indeterminate color="primary" /></div>
        <div v-else-if="!sheets.length" class="text--secondary">{{ $t('firmware.test.none-for-firmware') }}</div>
        <v-simple-table v-else dense>
          <tbody>
            <tr v-for="sheet in sheets" :key="sheet.id" class="clickable" @click="show(sheet)">
              <td>
                <div class="font-weight-medium">{{ sheet.chargingPointName || sheet.chargingPointId }}</div>
                <div class="text-caption text--secondary">{{ [sheet.vendor, sheet.model].filter(v => v).join(' · ') || '-' }}</div>
              </td>
              <td><v-chip x-small :color="STATUS_COLORS[sheet.status]" text-color="white">{{ $t('firmware.test.statuses.' + sheet.status) }}</v-chip></td>
              <td class="text-caption">{{ $t('firmware.test.progress', {done: done(sheet), total: sheet.lines.length}) }}</td>
              <td class="text-caption text--secondary">{{ $t('firmware.test.started', {by: sheet.startedByName || '-', date: formatDate(sheet.startedAt)}) }}</td>
              <td style="width: 1%"><v-icon small>mdi-chevron-right</v-icon></td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="open" max-width="860" scrollable>
      <v-card v-if="open">
        <v-card-title class="d-flex align-center flex-wrap" style="gap: 12px">
          <div class="flex-grow-1">
            <div class="text-overline text--secondary" style="line-height: 1.4">{{ $t('firmware.test.title') }}</div>
            <div class="text-h6" style="line-height: 1.3">{{ current.chargingPointName || current.chargingPointId }}</div>
          </div>
          <v-btn icon small :loading="loading" :title="$t('refresh')" @click="load"><v-icon small>mdi-refresh</v-icon></v-btn>
          <v-btn icon small :aria-label="$t('close')" @click="open = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <p class="text-body-2 text--secondary">{{ $t('firmware.test.explained') }}</p>

          <template v-if="current">
            <v-alert :color="STATUS_COLORS[current.status]" text dense>
              <div class="d-flex align-center flex-wrap" style="gap: 12px">
                <div class="flex-grow-1">
                  <div class="font-weight-medium">
                    {{ $t('firmware.test.statuses.' + current.status) }}
                    <template v-if="current.status === 'open'"> · {{ $t('firmware.test.progress', {done: done(current), total: current.lines.length}) }}</template>
                  </div>
                  <div class="text-caption">
                    {{ $t('firmware.test.version', {version: current.version || '-', model: [current.vendor, current.model].filter(v => v).join(' · ') || '-'}) }}
                  </div>
                  <div class="text-caption text--secondary">
                    {{ $t('firmware.test.started', {by: current.startedByName || '-', date: formatDate(current.startedAt)}) }}
                    <template v-if="current.status === 'open'"> · {{ $t('firmware.test.due', {date: formatDate(current.dueAt)}) }}</template>
                    <template v-else-if="current.closedAt"> · {{ $t('firmware.test.closed-on', {date: formatDate(current.closedAt), by: current.closedBy || $t('firmware.test.automatically')}) }}</template>
                  </div>
                </div>
                <template v-if="canDeployFirmware">
                  <v-btn v-if="current.status === 'open'" small outlined :loading="closing" @click="close">{{ $t('firmware.test.close') }}</v-btn>
                  <v-btn v-else small color="primary" :loading="starting" @click="start(null, current.chargingPointId)">{{ $t('firmware.test.restart') }}</v-btn>
                </template>
              </div>
            </v-alert>

            <template v-for="group in groups">
              <div :key="group.key + '-title'" class="text-subtitle-2 mt-4 mb-1">
                <v-icon small class="mr-1">{{ group.icon }}</v-icon>{{ $t('firmware.test.groups.' + group.key) }}
              </div>
              <v-simple-table :key="group.key" dense class="lines">
                <tbody>
                  <tr v-for="line in group.lines" :key="line.code">
                    <td style="width: 32px"><v-icon :color="STATE_COLORS[line.state]">{{ STATE_ICONS[line.state] }}</v-icon></td>
                    <td>
                      <div class="font-weight-medium">{{ $t('firmware.test.checks.' + line.code + '.label') }}</div>
                      <div class="text-caption text--secondary">{{ $t('firmware.test.checks.' + line.code + '.how') }}</div>
                      <div v-if="line.manual" class="text-caption">
                        <v-icon x-small>mdi-account</v-icon> {{ $t('firmware.test.marked', {by: line.by || '-', state: $t('firmware.test.states.' + line.state)}) }}<template v-if="line.note"> — {{ line.note }}</template>
                      </div>
                    </td>
                    <td class="text-caption text-right" style="white-space: nowrap">
                      <span v-if="line.evidence" class="evidence">{{ line.evidence }}</span>
                      <div v-if="line.at && !line.manual" class="text--secondary">{{ formatDate(line.at, 'DD/MM HH:mm:ss') }}</div>
                    </td>
                    <td v-if="canDeployFirmware && current.status === 'open'" style="width: 40px">
                      <v-menu left offset-y>
                        <template #activator="{on, attrs}">
                          <v-btn icon small v-bind="attrs" v-on="on" :aria-label="$t('firmware.test.decide')"><v-icon small>mdi-dots-vertical</v-icon></v-btn>
                        </template>
                        <v-list dense>
                          <v-list-item v-for="state in MANUAL_STATES" :key="state" @click="decide(line, state)">
                            <v-list-item-icon class="mr-2"><v-icon small :color="STATE_COLORS[state]">{{ STATE_ICONS[state] }}</v-icon></v-list-item-icon>
                            <v-list-item-title>{{ $t('firmware.test.mark-as', {state: $t('firmware.test.states.' + state)}) }}</v-list-item-title>
                          </v-list-item>
                          <v-list-item v-if="line.manual" @click="decide(line, null)">
                            <v-list-item-icon class="mr-2"><v-icon small>mdi-backup-restore</v-icon></v-list-item-icon>
                            <v-list-item-title>{{ $t('firmware.test.back-to-messages') }}</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </template>

          </template>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Rouge ou sans objet : on dit pourquoi -->
    <v-dialog v-model="noteDialog" max-width="480">
      <v-card v-if="pending">
        <v-card-title class="text-subtitle-1">{{ $t('firmware.test.checks.' + pending.line.code + '.label') }} — {{ $t('firmware.test.states.' + pending.state) }}</v-card-title>
        <v-card-text>
          <v-textarea v-model="pending.note" :label="$t('firmware.test.note')" rows="2" auto-grow autofocus outlined dense hide-details />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn small text @click="noteDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn small color="primary" :disabled="!pending.note || !pending.note.trim()" :loading="marking" @click="mark(pending.line, pending.state, pending.note)">{{ $t('save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import FirmwareRepository from "@repository/FirmwareRepository";
import FirmwareMixin from "@pages/Firmware/FirmwareMixin";
import {EventBus} from "@includes/EventBus";

/** Toutes les 10 secondes tant que la fiche est ouverte à l'écran : la personne devant la borne voit sa ligne passer au vert. */
const REFRESH_MS = 10000

/**
 * Les tests d'un firmware, dans sa fiche : on donne l'identifiant d'une borne qui l'a installé et on lance ses tests —
 * une ligne par fonction, remplie par ses messages OCPP depuis l'ouverture ; certaines demandent quelqu'un sur place
 * (badge, charge). L'historique des fiches du firmware reste ici. Une borne n'a pas à être testée : on en éprouve
 * quelques-unes par modèle.
 */
export default {
  mixins: [FirmwareMixin],

  props: {
    firmware: {type: Object, required: true},
    // Les bornes qui ont installé ce firmware : on propose leurs identifiants.
    chargingPoints: {type: Array, default: () => []},
  },

  data() {
    return {
      sheets: [],
      identifier: '',
      selectedId: null,
      loading: false,
      open: false,
      starting: false,
      closing: false,
      marking: false,
      noteDialog: false,
      pending: null,
      listeners: [],
      timer: null,
      STATUS_COLORS: {open: 'primary', passed: 'success', failed: 'error', incomplete: 'orange', interrupted: 'grey'},
      STATE_COLORS: {pending: 'grey lighten-1', passed: 'success', failed: 'error', 'not-applicable': 'blue-grey'},
      STATE_ICONS: {pending: 'mdi-checkbox-blank-circle-outline', passed: 'mdi-check-circle', failed: 'mdi-close-circle', 'not-applicable': 'mdi-minus-circle-outline'},
      MANUAL_STATES: ['passed', 'failed', 'not-applicable'],
    }
  },

  computed: {
    current() {
      return this.sheets.find(sheet => sheet.id === this.selectedId) || null
    },
    // À distance d'abord (ça se remplit seul), puis sur place.
    groups() {
      const lines = this.current ? this.current.lines : []
      return [
        {key: 'remote', icon: 'mdi-access-point', lines: lines.filter(line => !line.onSite)},
        {key: 'on-site', icon: 'mdi-account-hard-hat', lines: lines.filter(line => line.onSite)},
      ]
    },
    identifierItems() {
      return this.chargingPoints.filter(chargingPoint => chargingPoint.identifier).map(chargingPoint => ({value: chargingPoint.identifier, text: `${chargingPoint.identifier} — ${chargingPoint.name}`}))
    },
    watching() {
      return this.open && !!this.current && this.current.status === 'open'
    },
  },

  mounted() {
    this.load()
  },

  destroyed() {
    this.unlisten()
    this.schedule(false)
  },

  watch: {
    watching(value) {
      this.schedule(value)
    },
    open(value) {
      if (!value) {
        this.unlisten()
      }
    },
    'firmware.id'() {
      this.sheets = []
      this.load()
    },
  },

  methods: {
    load() {
      this.loading = true
      return FirmwareRepository.testSheetsOfFirmware(this.firmware.id).then(sheets => {
        this.sheets = sheets
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.loading = false
      })
    },

    show(sheet) {
      this.selectedId = sheet.id
      this.open = true
      this.listen(sheet.chargingPointId)
    },

    // Une borne par son identifiant OCPP (ou, pour relancer, par son id) ; la nouvelle fiche s'ouvre.
    start(identifier, chargingPointId = null) {
      this.starting = true
      const typed = identifier && typeof identifier === 'object' ? identifier.value : identifier
      FirmwareRepository.startTestSheet({firmwareVersionId: this.firmware.id, chargingPointIdentifier: typed ? typed.trim() : null, chargingPointId}).then(({id}) => {
        this.identifier = ''
        return this.load().then(() => {
          const sheet = this.sheets.find(item => item.id === id)
          if (sheet) {
            this.show(sheet)
          }
        })
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.starting = false
      })
    },

    close() {
      this.closing = true
      FirmwareRepository.closeTestSheet(this.current.id).then(() => {
        this.load()
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.closing = false
      })
    },

    // Vert ou rendu aux messages : tout de suite ; rouge ou sans objet : avec une note.
    decide(line, state) {
      if (state === null || state === 'passed') {
        this.mark(line, state, null)
        return
      }
      this.pending = {line, state, note: ''}
      this.noteDialog = true
    },

    mark(line, state, note) {
      this.marking = true
      FirmwareRepository.markTestCheck(this.current.id, line.code, state, note).then(() => {
        this.noteDialog = false
        this.load()
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.marking = false
      })
    },

    done(sheet) {
      return sheet.lines.filter(line => line.state !== 'pending').length
    },

    schedule(active) {
      if (active && !this.timer) {
        this.timer = setInterval(() => this.load(), REFRESH_MS)
      } else if (!active && this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },

    // Les messages de la borne poussés au navigateur rafraîchissent la fiche ouverte sans attendre.
    listen(chargingPointId) {
      this.unlisten()
      this.listeners = ['StatusNotification', 'MeterValues', 'GetConfiguration', 'BootNotification'].map(action => {
        const token = 'on-' + action + '-' + chargingPointId
        EventBus.$on(token, () => {
          if (this.watching) {
            this.load()
          }
        })
        return token
      })
    },

    unlisten() {
      this.listeners.forEach(token => EventBus.$off(token))
      this.listeners = []
    },
  },
}
</script>

<style scoped lang="scss">
.evidence, .mono {
  font-family: monospace;
}
.clickable {
  cursor: pointer;
}
</style>
